/** Regles completes. La page que cherche un joueur qui veut le bareme exact. */
import type { Page } from '../types.js';

export const HOW_TO_PLAY: Page = {
  title: 'Comment jouer à Retusch',
  summary:
    'Les règles complètes de Retusch : le déroulé d’une manche, le barème de points côté dessinateur et côté chercheur, les outils de dessin et les conseils pour se cacher comme pour traquer.',
  sections: [
    {
      heading: 'Le concept en trois phrases',
      blocks: [
        'On vous donne un tableau dont une case a été effacée, et vous la repeignez pour qu’elle passe inaperçue.',
        'Ensuite, tout le monde regarde les tableaux retouchés et cherche les cases repeintes.',
        'Vous marquez en trouvant vite, et vous marquez aussi tant que votre propre cachette tient.',
      ],
    },
    {
      heading: 'Le déroulé d’une manche',
      blocks: [
        'Une partie enchaîne des manches identiques, cinq par défaut. Chacune passe par quatre temps.',
        {
          list: [
            'Le dessin. Vous recevez votre tableau et votre case, et vous avez 2 minutes par défaut pour la repeindre. Le bouton Terminé vaut validation : dès que tout le monde a validé, la manche avance sans attendre la fin du chrono.',
            'La traque. Le tableau réapparaît avec les retouches, et vous avez 30 secondes par défaut pour les repérer. Un clic sur une case repeinte la révèle à toute la table.',
            'La révélation. Toutes les cachettes sont encadrées et nommées, y compris celles que personne n’a vues. Un curseur permet de comparer le tableau retouché et l’œuvre d’origine.',
          ],
        },
      ],
    },
    {
      heading: 'Le barème des points',
      blocks: [
        'Deux compteurs tournent en même temps, et ils s’opposent : celui qui cherche veut aller vite, celui qui a dessiné veut que sa case tienne.',
        {
          list: [
            'Côté chercheur : 1000 points si vous trouvez dans les 3 premières secondes, puis une décroissance régulière jusqu’à 100 points à la toute fin de la phase. Rater ou ne rien trouver ne rapporte rien.',
            'Côté dessinateur : 100 points seulement si votre case tombe immédiatement, puis une progression régulière jusqu’à 800 points si elle tient jusqu’au bout. Une retouche que personne ne trouve vaut le maximum, 800 points.',
          ],
        },
        'Le dessinateur n’est payé qu’une fois, à la révélation, et sur le chercheur le plus rapide. Peu importe donc que deux ou huit personnes vous cherchent : seule compte celle qui vous trouve en premier.',
        'Ce barème est le même quelle que soit la durée réglée pour la phase. Allonger le temps de recherche ne change pas les bornes, seulement la pente entre les deux.',
        'Une seule erreur par manche. Le premier clic qui ne touche aucune retouche met fin à votre traque : sans cette règle, on ratisserait l’image au hasard jusqu’à tomber dessus. Deux gestes ne coûtent rien : cliquer sur votre propre case, et cliquer sur une retouche déjà trouvée.',
      ],
    },
    {
      heading: 'Les outils de dessin',
      blocks: [
        {
          list: [
            'Le pinceau (B) : trois tailles rapides, plus un curseur pour la taille exacte. Les raccourcis 1, 2 et 3 sélectionnent les trois tailles.',
            'Le pot de peinture (P) : remplit d’un coup une surface unie, sans jamais déborder de votre case.',
            'La pipette (I) : prélève une couleur n’importe où sur le tableau. Maintenir Alt y bascule le temps d’un clic, puis rend la main à l’outil précédent.',
            'La main (H) : déplace la vue. Hors de votre case, le pinceau et le pot font la même chose, vous n’avez donc presque jamais besoin de changer d’outil.',
            'L’annulation (Ctrl+Z ou Cmd+Z) : revient en arrière trait par trait, jusqu’à trente fois. Il n’y a pas de rétablissement.',
            'La pastille de fond, en fin de palette : c’est la couleur de départ de votre case. Repeindre avec elle revient à effacer, à ceci près que c’est un trait comme un autre, qui s’annule et se recouvre. Il n’y a donc pas de gomme.',
          ],
        },
      ],
    },
    {
      heading: 'Cinq conseils pour se cacher',
      blocks: [
        {
          list: [
            'Prélevez, ne devinez pas. La pipette donne la couleur exacte du voisinage : un aplat à peine décalé se repère de loin, alors qu’un aplat juste devient invisible.',
            'Prolongez ce qui traverse votre case. Un mur, un horizon, une branche qui entre d’un côté doit ressortir de l’autre au bon endroit. Une ligne interrompue est le premier indice que cherche un joueur expérimenté.',
            'Peignez les coins en premier. C’est l’erreur la plus courante : le centre est soigné, les quatre angles restent au fond d’origine, et le carré ressort à l’œil nu sans même qu’on ait à regarder le dessin.',
            'Allez du général au détail. Posez d’abord les grandes masses de couleur au gros pinceau, puis affinez. L’inverse mange tout votre temps sur un coin pendant que le reste reste vide.',
            'Copiez le niveau de détail des alentours. Une case trop propre au milieu d’une zone chargée se voit autant qu’une case bâclée au milieu d’un aplat.',
          ],
        },
      ],
    },
    {
      heading: 'Quatre conseils pour traquer',
      blocks: [
        {
          list: [
            'Cherchez les lignes qui s’arrêtent. Une bordure, un câble, un bord de table qui se décale d’un cheveu : c’est presque toujours là.',
            'Balayez d’abord, zoomez ensuite. À pleine vue, une case mal fondue apparaît comme une tache. Le zoom sert à confirmer, pas à chercher.',
            'Méfiez-vous du trop lisse. Une surface parfaitement unie au milieu d’une texture est aussi suspecte qu’une bavure.',
            'Ne cliquez pas au hasard. Vous n’avez droit qu’à une erreur : mieux vaut perdre trois secondes à vérifier que perdre la manche entière.',
          ],
        },
      ],
    },
    {
      heading: 'Et selon le mode ?',
      blocks: [
        'Tout ce qui précède décrit la chasse commune, le mode par défaut. Les autres modes changent qui dessine, qui cherche et comment on gagne : le détail de chacun est sur la page des modes de jeu.',
      ],
    },
  ],
};
